import type {
  CreateMediaRequestAPI,
  CreateMediaRequestResponseAPI,
  CreateSharedMediaRequestAPI,
  DriverFaceClustersMediaAPI,
  MediaAPI,
  MediaEventVideoAPI,
  MediaFootageVideoAPI,
  MediaViewAPI,
} from './definitions/APITypes';

import {
  type CreateMediaRequest,
  type CreateMediaRequestResponse,
  type CreateSharedMedia,
  EventSeverity,
  type Media,
  type MediaEvent,
  type MediaRequest,
  MediaRequestStatus,
  MediaType,
  type MediaView,
  MediaWatchStatus,
  type PageableContent,
  type PageableContentAPI,
  Severity,
} from '@cobliteam/common-types';

import { driverAPIToDriver } from '../driver/APITransformer';

const toMediaRequestStatus: { [key: string]: MediaRequestStatus } = {
  PENDING: MediaRequestStatus.PENDING,
  ACKNOWLEDGE: MediaRequestStatus.ACKNOWLEDGE,
  STARTED: MediaRequestStatus.STARTED,
  FINISHED: MediaRequestStatus.FINISHED,
  UNAVAILABLE: MediaRequestStatus.UNAVAILABLE,
  FAILED: MediaRequestStatus.FAILED,
  CANCELED: MediaRequestStatus.CANCELED,
};

const eventSeverityToSeverity = (severity: EventSeverity): Severity => {
  switch (severity) {
    case EventSeverity.HIGHER:
      return Severity.VERY_SEVERE;
    case EventSeverity.HIGH:
      return Severity.SEVERE;
    case EventSeverity.MEDIUM:
      return Severity.MEDIUM;
    default:
      return Severity.VERY_SEVERE;
  }
};

export const mediaNewFilter = (mediaStatus?: MediaWatchStatus[]) => {
  if (mediaStatus) {
    const isNewFilter = mediaStatus.includes(MediaWatchStatus.NEW);
    const isWatchedFilter = mediaStatus.includes(MediaWatchStatus.WATCHED);
    if (isNewFilter && !isWatchedFilter) {
      return true;
    }
    if (!isNewFilter && isWatchedFilter) {
      return false;
    }
  }

  return undefined;
};

export const driverFaceClusterAPIToDriverFaceCluster = (cluster?: DriverFaceClustersMediaAPI) => {
  if (!cluster) {
    return;
  }

  return {
    driver: cluster.driver ? driverAPIToDriver(cluster.driver) : undefined,
    id: cluster.id,
    profileImage: {
      imageUrl: cluster.profile_image_url.image_url,
    },
  };
};

const statusToMediaRequestStatus = (status: string): MediaRequestStatus => toMediaRequestStatus[status] || MediaRequestStatus.PENDING;

export const toMedia = (mediaAPI: MediaAPI): Media => {
  const commonFields = {
    id: mediaAPI.id,
    deviceId: mediaAPI.device_id,
    driverId: mediaAPI.driver_id,
    driverName: mediaAPI.driver_name,
    mediaType: mediaAPI.media_type,
    mediaViewCount: mediaAPI.media_view_count,
    timestamp: mediaAPI.timestamp,
    driverFaceCluster: driverFaceClusterAPIToDriverFaceCluster(mediaAPI.driver_face_cluster),
    licensePlate: mediaAPI.vehicle?.licensePlate,
    group: mediaAPI?.vehicle?.groups?.length ? mediaAPI.vehicle?.groups[0].name : undefined,
    isFavorite: mediaAPI.is_favorite,
    vehicle: mediaAPI.vehicle,
  };

  switch (mediaAPI.media_type) {
    case MediaType.TIMELAPSE:
    case MediaType.FOOTAGE_VIDEO:
      const videoFootageAPI = mediaAPI.media_details as MediaFootageVideoAPI;
      const createdBy = videoFootageAPI.created_by
        ? {
            id: videoFootageAPI.created_by.id,
            fleetId: videoFootageAPI.created_by.fleet_id,
            emailAddress: videoFootageAPI.created_by.email_address,
            fullName: videoFootageAPI.created_by.full_name,
          }
        : undefined;

      return {
        ...commonFields,
        mediaDetails: {
          createdAt: videoFootageAPI.created_at,
          duration: videoFootageAPI.duration,
          endLatitude: videoFootageAPI.end_latitude,
          endLongitude: videoFootageAPI.end_longitude,
          mediaUrl: videoFootageAPI.footage_media_url,
          requestId: videoFootageAPI.request_id,
          startLatitude: videoFootageAPI.start_latitude,
          startLongitude: videoFootageAPI.start_longitude,
          status: statusToMediaRequestStatus(videoFootageAPI.status),
          thumbnailUrl: videoFootageAPI.footage_thumbnail_url,
          updatedAt: videoFootageAPI.updated_at,
          createdBy,
        } as MediaRequest,
      };

    case MediaType.EVENT_VIDEO:
      const videoEventAPI = mediaAPI.media_details as MediaEventVideoAPI;

      return {
        ...commonFields,
        severity: eventSeverityToSeverity(mediaAPI.severity!),
        mediaDetails: {
          eventTrigger: videoEventAPI.event_trigger,
          latitude: videoEventAPI.latitude,
          longitude: videoEventAPI.longitude,
          mediaUrl: videoEventAPI.event_media_url,
          speed: videoEventAPI.speed,
          speedSign: videoEventAPI.speed_sign,
          thumbnailUrl: videoEventAPI.event_thumbnail_url,
        } as MediaEvent,
      };
  }
};

export const toMediaPage = (page: PageableContentAPI<MediaAPI>): PageableContent<Media> => ({
  content: page.content.map(toMedia),
  last: page.last,
  totalPages: page.total_pages,
  totalElements: page.total_elements,
  numberOfElements: page.number_of_elements,
  number: page.number,
  size: page.size,
  first: page.first,
  empty: page.empty,
});

export const toCreateMediaRequestResponse = (response: CreateMediaRequestResponseAPI): CreateMediaRequestResponse => ({
  vehicleId: response.vehicle_id,
  startTime: response.start_time,
  endTime: response.end_time,
  mediaId: response.media_id,
});

export const toMediaView = (response: MediaViewAPI): MediaView => ({
  mediaUrl: response.media_url,
  expirationDate: response.expiration_date,
  mediaInfo: toMedia(response.media_info),
  videos: response.videos,
});

export const toCreateMediaRequestAPI = (request: CreateMediaRequest): CreateMediaRequestAPI => ({
  vehicle_id: request.vehicleId,
  start_time: request.startTime,
  end_time: request.endTime,
  is_timelapse: request.isTimelapse,
  enable_email_notification: request.enableEmailNotification,
  trip_id: request.tripId,
});

export const toCreateSharedMediaRequestAPI = (request: CreateSharedMedia): CreateSharedMediaRequestAPI => ({
  media_id: request.mediaId,
});
